import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { cabecalhoEmpresa, patch, post } from '../../nucleo/api';
import { campoEmpresaDoRegistro, empresaEscolhida } from '../../nucleo/empresa/empresa-do-registro';
import { nomeCadastro } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { ItensEditor, linhasParaApi } from './ItensEditor';
import { invalidarCompras, novaLinha, type LinhaItem, type RequisicaoDetalhe } from './tipos';

/** Drawer de nova requisição (M10.1) ou de edição do rascunho. O id nasce aqui: reenviar o formulário nunca cria uma segunda. */
export function FormRequisicao({ aberto, requisicao, aoFechar, aoSalvar }: { aberto: boolean; requisicao?: RequisicaoDetalhe | null; aoFechar: () => void; aoSalvar?: (id: string) => void }) {
  if (!aberto) return null;
  return <Corpo requisicao={requisicao ?? null} aoFechar={aoFechar} aoSalvar={aoSalvar} />;
}

function Corpo({ requisicao, aoFechar, aoSalvar }: { requisicao: RequisicaoDetalhe | null; aoFechar: () => void; aoSalvar?: (id: string) => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const [novoId] = useState(() => crypto.randomUUID());
  const [linhas, setLinhas] = useState<LinhaItem[]>(
    requisicao
      ? requisicao.itens_detalhe.map((i) => ({ ...novaLinha(), produto_id: i.produto_id, produto_rotulo: `${i.produto_codigo ?? ''} ${nomeCadastro(i.produto_nome)}`.trim(), quantidade: String(i.quantidade).replace('.', ',') }))
      : [novaLinha()],
  );

  const campos: DefCampo[] = [
    // V2 verticais: requisição nova sem empresa na aba (sem filtro, várias permitidas) pede a empresa.
    ...(requisicao ? [] : campoEmpresaDoRegistro(s.empresas, { rotulo: tn('empresa.rotulo'), ajuda: tn('empresa.ajudaRegistro') })),
    { nome: 'solicitante_nome', rotulo: t('campos.solicitante'), tipo: 'texto', ajuda: t('ajuda.solicitante') },
    { nome: 'justificativa', rotulo: t('campos.justificativa'), tipo: 'area', obrigatorio: true, ajuda: t('ajuda.justificativa') },
  ];

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={requisicao ? t('requisicao.editarTitulo', { numero: requisicao.numero }) : t('acoes.novaRequisicao')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-requisicao">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        id="form-requisicao"
        campos={campos}
        telaCampos="compras.requisicao"
        entidade="compras.requisicao"
        inicial={requisicao ? { solicitante_nome: requisicao.solicitante_nome, justificativa: requisicao.justificativa, extras: requisicao.extras } : {}}
        aoEnviar={async (v) => {
          const itens = linhasParaApi(linhas, { preco: false, prazo: false }, { semItens: t('erros.semItens'), incompleto: t('erros.itemIncompleto') });
          const corpo = { solicitante_nome: String(v.solicitante_nome ?? '').trim() || undefined, justificativa: String(v.justificativa ?? '').trim(), itens, extras: v.extras ?? {} };
          let id: string = novoId;
          if (requisicao) {
            id = requisicao.id;
            await patch(`/compras/requisicoes/${id}`, corpo);
          } else {
            await post('/compras/requisicoes', { id, ...corpo }, cabecalhoEmpresa(empresaEscolhida(v)));
          }
          await invalidarCompras(qc);
          toast.sucesso(tn('comum.salvo'));
          aoSalvar?.(id);
          aoFechar();
        }}
      >
        <ItensEditor linhas={linhas} aoMudar={setLinhas} moeda={s.workspace.moeda} />
      </Formulario>
    </Drawer>
  );
}
